// The storefront is PER-ORG: each org configures its own Shopify Storefront
// access token + domain in the org integrations vault (same place as Notion /
// Shopify-admin creds). There is NO hardcoded global token and NO fallback —
// callers must configure the active store (via setStorefrontConfig, normally
// through loadStorefrontConfig in src/features/store/storeConfig) before
// fetching. When unconfigured, reads return empty and the UI shows "not
// connected", so a missing config can never silently fall back to another
// org's catalog. Storefront tokens are publishable (designed for browser use),
// so exposing the active org's token client-side is expected and safe; the
// Shopify ADMIN clientId/clientSecret are never sent to the browser.
export interface StorefrontConfig {
  domain: string;        // e.g. your-store.myshopify.com
  storefrontToken: string;
  apiVersion: string;    // e.g. 2024-01
}

let activeConfig: StorefrontConfig | null = null;

/** Configure (or clear) the active store. Switching stores clears the product
 *  cache and in-memory cart so one org's catalog/cart never bleeds into another. */
export function setStorefrontConfig(cfg: StorefrontConfig | null): void {
  const nextDomain = cfg?.domain || null;
  if (nextDomain !== (activeConfig?.domain || null)) {
    cachedProducts = null;
    cacheTime = 0;
    cachedDomain = null;
    currentCart = null; // cart is store-specific
  }
  activeConfig = cfg && cfg.domain && cfg.storefrontToken ? { ...cfg, apiVersion: cfg.apiVersion || '2024-01' } : null;
}

export function isStorefrontConfigured(): boolean {
  return !!activeConfig;
}

/** Drop the in-memory product cache so the next fetchAllProducts() hits the
 *  network (used after Admin-side writes so edits show without a reload). */
export function invalidateProductsCache(): void {
  cachedProducts = null;
  cacheTime = 0;
}

export function getStorefrontDomain(): string | null {
  return activeConfig?.domain || null;
}

export interface StoreProduct {
  id: string;
  title: string;
  handle: string;
  descriptionHtml: string;
  productType: string;
  vendor: string;
  availableForSale: boolean;
  collections: { id: string; title: string; handle: string }[];
  images: { src: string; altText: string | null }[];
  variants: {
    id: string;
    title: string;
    price: { amount: string; currencyCode: string };
    compareAtPrice: { amount: string; currencyCode: string } | null;
    availableForSale: boolean;
  }[];
}

export interface CartLine {
  id: string;
  variantId: string;
  productTitle: string;
  variantTitle: string;
  quantity: number;
  price: { amount: string; currencyCode: string };
  image?: { src: string; altText: string | null };
}

export interface Cart {
  id: string;
  checkoutUrl: string;
  lines: CartLine[];
  totalQuantity: number;
  totalAmount: { amount: string; currencyCode: string };
}

let cachedProducts: StoreProduct[] | null = null;
let cacheTime = 0;
let cachedDomain: string | null = null;
const CACHE_TTL = 5 * 60 * 1000;

async function storefrontFetch(query: string, variables?: Record<string, unknown>) {
  if (!activeConfig) throw new Error('Storefront not configured for this store.');
  const endpoint = `https://${activeConfig.domain}/api/${activeConfig.apiVersion}/graphql.json`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': activeConfig.storefrontToken,
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error(`Storefront API error: ${res.status}`);
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0]?.message || 'GraphQL error');
  return json.data;
}

export async function fetchAllProducts(): Promise<StoreProduct[]> {
  // No active store config → no catalog. Never fall back to another store.
  if (!activeConfig) return [];
  if (cachedProducts && cachedDomain === activeConfig.domain && Date.now() - cacheTime < CACHE_TTL) return cachedProducts;

  const data = await storefrontFetch(`{
    products(first: 100) {
      edges {
        node {
          id
          title
          handle
          descriptionHtml
          productType
          vendor
          availableForSale
          collections(first: 10) {
            edges { node { id title handle } }
          }
          images(first: 20) {
            edges {
              node {
                src
                altText
              }
            }
          }
          variants(first: 50) {
            edges {
              node {
                id
                title
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
                availableForSale
              }
            }
          }
        }
      }
    }
  }`);

  cachedProducts = data.products.edges.map((e: any) => {
    const n = e.node;
    return {
      id: n.id,
      title: n.title,
      handle: n.handle,
      descriptionHtml: n.descriptionHtml,
      productType: n.productType,
      vendor: n.vendor,
      availableForSale: n.availableForSale,
      collections: (n.collections?.edges || []).map((ce: any) => ({ id: ce.node.id, title: ce.node.title, handle: ce.node.handle })),
      images: n.images.edges.map((ie: any) => ({
        src: ie.node.src,
        altText: ie.node.altText,
      })),
      variants: n.variants.edges.map((ve: any) => ({
        id: ve.node.id,
        title: ve.node.title,
        price: ve.node.price,
        compareAtPrice: ve.node.compareAtPrice,
        availableForSale: ve.node.availableForSale,
      })),
    } satisfies StoreProduct;
  });
  cacheTime = Date.now();
  cachedDomain = activeConfig.domain;
  return cachedProducts!;
}

const CART_FRAGMENT = `
  fragment CartFields on Cart {
    id
    checkoutUrl
    totalQuantity
    cost { totalAmount { amount currencyCode } }
    lines(first: 50) {
      edges {
        node {
          id
          quantity
          merchandise {
            ... on ProductVariant {
              id
              title
              price { amount currencyCode }
              product { title }
              image { src altText }
            }
          }
        }
      }
    }
  }
`;

function parseCart(c: any): Cart {
  return {
    id: c.id,
    checkoutUrl: c.checkoutUrl,
    totalQuantity: c.totalQuantity,
    totalAmount: c.cost.totalAmount,
    lines: c.lines.edges.map((e: any) => {
      const n = e.node;
      const m = n.merchandise;
      return {
        id: n.id,
        variantId: m.id,
        productTitle: m.product.title,
        variantTitle: m.title,
        quantity: n.quantity,
        price: m.price,
        image: m.image ? { src: m.image.src, altText: m.image.altText } : undefined,
      };
    }),
  };
}

let currentCart: Cart | null = null;
let currentUserId: string | null = null;

function cartStorageKey(): string {
  // Scope the cart to the active store domain so different orgs' carts (and
  // their Shopify checkout URLs) never collide in localStorage.
  const store = activeConfig?.domain || 'unset';
  return currentUserId ? `shopify_cart_id_${store}_${currentUserId}` : `shopify_cart_id_${store}`;
}

function getStoredCartId(): string | null {
  try { return localStorage.getItem(cartStorageKey()); } catch { return null; }
}
function setStoredCartId(id: string | null) {
  try {
    if (id) localStorage.setItem(cartStorageKey(), id);
    else localStorage.removeItem(cartStorageKey());
  } catch { /* ignore */ }
}

/** Set the current user for per-user cart storage. Call on login/logout. */
export function setCartUser(userId: string | null) {
  if (currentUserId === userId) return;
  currentUserId = userId;
  currentCart = null; // clear in-memory cache so next getCart() loads from correct key
}

/** Clear cart state entirely (e.g. on logout) */
export function clearCartForLogout() {
  currentCart = null;
  currentUserId = null;
}

export async function getCart(): Promise<Cart | null> {
  if (currentCart) return currentCart;
  const cartId = getStoredCartId();
  if (!cartId) return null;
  try {
    const data = await storefrontFetch(`
      query getCart($id: ID!) { cart(id: $id) { ...CartFields } }
      ${CART_FRAGMENT}
    `, { id: cartId });
    if (!data.cart) {
      setStoredCartId(null);
      return null;
    }
    currentCart = parseCart(data.cart);
    return currentCart;
  } catch {
    setStoredCartId(null);
    return null;
  }
}

async function createCart(variantId: string, quantity: number): Promise<Cart> {
  const data = await storefrontFetch(`
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { ...CartFields }
        userErrors { message field }
      }
    }
    ${CART_FRAGMENT}
  `, {
    input: {
      lines: [{ merchandiseId: variantId, quantity }],
    },
  });
  const errors = data.cartCreate.userErrors;
  if (errors?.length) throw new Error(errors[0].message);
  currentCart = parseCart(data.cartCreate.cart);
  setStoredCartId(currentCart.id);
  return currentCart;
}

export async function addToCart(variantId: string, quantity = 1): Promise<Cart> {
  const cart = await getCart();
  if (!cart) return createCart(variantId, quantity);

  // Check if variant already in cart
  const existingLine = cart.lines.find((l) => l.variantId === variantId);
  if (existingLine) {
    return updateCartLine(existingLine.id, existingLine.quantity + quantity);
  }

  const data = await storefrontFetch(`
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
        userErrors { message field }
      }
    }
    ${CART_FRAGMENT}
  `, {
    cartId: cart.id,
    lines: [{ merchandiseId: variantId, quantity }],
  });
  const errors = data.cartLinesAdd.userErrors;
  if (errors?.length) throw new Error(errors[0].message);
  currentCart = parseCart(data.cartLinesAdd.cart);
  return currentCart;
}

export async function updateCartLine(lineId: string, quantity: number): Promise<Cart> {
  const cart = await getCart();
  if (!cart) throw new Error('No cart');
  const data = await storefrontFetch(`
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
        userErrors { message field }
      }
    }
    ${CART_FRAGMENT}
  `, {
    cartId: cart.id,
    lines: [{ id: lineId, quantity }],
  });
  const errors = data.cartLinesUpdate.userErrors;
  if (errors?.length) throw new Error(errors[0].message);
  currentCart = parseCart(data.cartLinesUpdate.cart);
  return currentCart;
}

export async function removeCartLine(lineId: string): Promise<Cart> {
  const cart = await getCart();
  if (!cart) throw new Error('No cart');
  const data = await storefrontFetch(`
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ...CartFields }
        userErrors { message field }
      }
    }
    ${CART_FRAGMENT}
  `, {
    cartId: cart.id,
    lineIds: [lineId],
  });
  const errors = data.cartLinesRemove.userErrors;
  if (errors?.length) throw new Error(errors[0].message);
  currentCart = parseCart(data.cartLinesRemove.cart);
  if (currentCart.totalQuantity === 0) {
    currentCart = null;
    setStoredCartId(null);
  }
  return currentCart!;
}

export async function clearAllCartLines(): Promise<null> {
  const cart = await getCart();
  if (!cart || cart.lines.length === 0) {
    currentCart = null;
    setStoredCartId(null);
    return null;
  }
  const data = await storefrontFetch(`
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ...CartFields }
        userErrors { message field }
      }
    }
    ${CART_FRAGMENT}
  `, {
    cartId: cart.id,
    lineIds: cart.lines.map((l) => l.id),
  });
  const errors = data.cartLinesRemove.userErrors;
  if (errors?.length) throw new Error(errors[0].message);
  currentCart = null;
  setStoredCartId(null);
  return null;
}

export function clearCartCache() {
  currentCart = null;
}

export function openCheckout(cart: Cart) {
  window.open(cart.checkoutUrl, '_blank', 'noopener');
}

export function optimizeImageUrl(src: string, width: number): string {
  return src.replace(/(\.\w+)(\?.*)?$/, `_${width}x$1$2`);
}
